import { useEffect, useRef, useState } from 'react';
import { useProgress } from '@/contexts/ProgressContext';
import { useAuth } from '@/hooks/useAuth';
import { progressSync } from '@/lib/storage-backend/progress-sync';
import type { BranchKey } from '@/lib/storage-backend/progress-sync';
import type { SyncConflictKind } from '@/components/toasts';
import type { Progress } from '@shared/schemas';

/**
 * Glue between `<ProgressProvider>` and the API sync orchestrator (FS slot).
 *
 *  - Local writes via existing setters fire IMMEDIATELY (already does — we
 *    just observe). React tree re-renders straight away ("optimistic UI").
 *  - On every `progress` mutation we diff against the last-synced snapshot BY
 *    VALUE (per-branch/-id serialized string · ADR-019) and enqueue only the
 *    branches whose value actually changed (debounced 200ms).
 *  - Sync only runs when `auth.isAuthed`.
 *  - After auth turns true, `progressSync.initialFetch()` pulls server truth
 *    and pushes it through `applyServerState`. `hydrated` stays false until
 *    that completes (caller blocks UI with ScreenLoader).
 *
 * ADR-019 (value diffing) · replaces the previous OBJECT-IDENTITY diff. Identity
 * diffing made "no code may recreate an object unless data changed" an invariant
 * — unenforceable (hydrate backfills, `ProgressSchema.parse`, and especially the
 * cross-tab `storage` listener re-parsing the whole graph all break it), and each
 * violation produced a full ~40-PATCH cascade. Comparing serialized VALUES makes
 * a rebuilt-but-identical graph produce zero requests, so the cross-tab / cross-
 * browser ping-pong that corrupted a `pro` student's completed sprint (P8-4-5)
 * becomes physically impossible: tab B gets from `storage` exactly what tab A
 * wrote — zero value divergence, zero PATCHes.
 *
 * Setter API for the rest of the app is unchanged · `ProgressContext` stays
 * frozen except for `applyServerState`.
 */

export interface UseProgressSyncResult {
  conflictKind: SyncConflictKind | null;
  dismissConflict: () => void;
  retryConflict: () => void;
  /**
   * `false` while the initial GET /progress is in flight after auth. The
   * caller (App.tsx Router) renders a ScreenLoader so the user can't make
   * edits that would be silently clobbered when the server snapshot arrives.
   * Goes `true` once initialFetch settles (success OR failure).
   */
  hydrated: boolean;
}

// ── Value-diffing map (ADR-019) ──────────────────────────────────────────────
// Each syncable node of `Progress` maps to a stable key (`branch` or `branch:id`)
// and a serialized value. `schedule` is called only when a key's serialized value
// differs from the last-synced map — never on object identity.

interface NodeKey {
  key: string;
  branch: BranchKey;
  id?: string;
}

/** Extract the value the given node syncs. Reads the LATEST progress at call
 *  time — the diff serializes it once; the schedule getter (P2) re-reads it at
 *  send time so a retry always ships the current value, never a stale snapshot. */
function readNode(p: Progress, branch: BranchKey, id?: string): unknown {
  switch (branch) {
    case 'last':
      return p.last;
    case 'tickets':
      return id ? p.tickets[id] : undefined;
    case 'sprints':
      return id ? p.sprints[id] : undefined;
    case 'homework':
      return id ? p.homework[id] : undefined;
    case 'onboarding':
      return p.onboarding;
    case 'pomodoro':
      return p.pomodoro;
    case 'cockpit':
      return id ? p.cockpit.flashcards?.[id] : p.cockpit;
    case 'achievements':
      return p.achievements;
    case 'capstone':
      return p.capstone['block-1'];
    case 'events': {
      const evs = p.events ?? [];
      // Server appends + dedupes-by-id + prunes 90d; send the current window.
      return evs.length > 0 ? { events: evs.slice(-500) } : undefined;
    }
    default:
      return undefined;
  }
}

/** Enumerate every syncable node present in `p` (including per-id branches). A
 *  key that vanishes from the state is simply absent here → never sent (the
 *  server has no delete path; behaviour unchanged from the identity-diff era). */
function collectKeys(p: Progress): NodeKey[] {
  const keys: NodeKey[] = [{ key: 'last', branch: 'last' }];
  for (const id of Object.keys(p.tickets)) keys.push({ key: `tickets:${id}`, branch: 'tickets', id });
  for (const id of Object.keys(p.sprints)) keys.push({ key: `sprints:${id}`, branch: 'sprints', id });
  for (const id of Object.keys(p.homework)) keys.push({ key: `homework:${id}`, branch: 'homework', id });
  keys.push({ key: 'onboarding', branch: 'onboarding' });
  if (p.pomodoro !== undefined) keys.push({ key: 'pomodoro', branch: 'pomodoro' });
  for (const id of Object.keys(p.cockpit.flashcards ?? {})) {
    keys.push({ key: `cockpit:${id}`, branch: 'cockpit', id });
  }
  // Whole-cockpit branch (mirrors the pre-ADR-019 behaviour: a flashcard edit
  // sends the flashcard AND the cockpit object; a non-flashcard cockpit edit
  // sends only the cockpit object).
  keys.push({ key: 'cockpit', branch: 'cockpit' });
  keys.push({ key: 'achievements', branch: 'achievements' });
  if (p.capstone['block-1']) keys.push({ key: 'capstone', branch: 'capstone' });
  if ((p.events ?? []).length > 0) keys.push({ key: 'events', branch: 'events' });
  return keys;
}

const serialize = (v: unknown): string => JSON.stringify(v ?? null);

/**
 * Serialize every syncable node → `Map<key, serialized>`. Exported as a test
 * seam: the ADR-019 invariant «a rebuilt-but-value-identical graph produces zero
 * PATCHes» is `buildValueMap(a)` deep-equals `buildValueMap(JSON-roundtrip(a))`.
 */
export function buildValueMap(p: Progress): Map<string, string> {
  const m = new Map<string, string>();
  for (const { key, branch, id } of collectKeys(p)) {
    m.set(key, serialize(readNode(p, branch, id)));
  }
  return m;
}

export function useProgressSync(): UseProgressSyncResult {
  const { progress, applyServerState } = useProgress();
  const auth = useAuth();
  const [conflictKind, setConflictKind] = useState<SyncConflictKind | null>(null);
  const [hydrated, setHydrated] = useState(false);
  /** Last-synced VALUE snapshot (key → serialized). Empty = not yet baselined. */
  const lastSyncedMapRef = useRef<Map<string, string>>(new Map());
  /** Latest progress · read by the schedule getters at send time (P2). */
  const progressRef = useRef(progress);
  progressRef.current = progress;
  // TODO ADR-019: кандидат на удаление после наблюдения — value-map rebaselines
  // itself on the refetch tick, so this flag is no longer load-bearing; kept as a
  // belt-and-suspenders guard until the storm is confirmed gone in the wild.
  const skipNextDiffRef = useRef(false);
  const applyServerStateRef = useRef(applyServerState);
  applyServerStateRef.current = applyServerState;

  // Wire events on mount · clear on unmount
  useEffect(() => {
    progressSync.setEvents({
      onConflict: (kind) => setConflictKind(kind),
      onRefetch: (next) => {
        // Rebaseline the value map to the server state so the next diff tick
        // sees zero divergence (no PATCH-storm-back of server-applied values).
        skipNextDiffRef.current = true;
        applyServerStateRef.current(next);
        lastSyncedMapRef.current = buildValueMap(next);
      },
    });
    return () => progressSync.setEvents(null);
  }, []);

  // Re-init on the authenticated user's IDENTITY, not just the authed boolean.
  // Supabase persists ONE session in shared localStorage across all tabs, so
  // logging into account B in another tab silently swaps THIS tab's session to
  // B. Keying on `userId` hard-resets the sync session on any identity change.
  const userId = auth.user?.id ?? null;
  useEffect(() => {
    if (!userId) {
      progressSync.setOwner(null);
      progressSync.setEnabled(false);
      lastSyncedMapRef.current = new Map();
      setHydrated(false);
      return;
    }
    progressSync.setEnabled(false);
    lastSyncedMapRef.current = new Map();
    progressSync.setOwner(userId); // bind writes to this account (multi-tab guard)
    progressSync.setEnabled(true);
    setHydrated(false);
    void progressSync.initialFetch().finally(() => setHydrated(true));
    return () => {
      progressSync.setOwner(null);
      progressSync.setEnabled(false);
    };
  }, [userId]);

  // Diff progress vs last-synced BY VALUE and enqueue only changed branches.
  // Suppressed until hydrated · re-baselines after onRefetch (skipNextDiff) and
  // on the first tick (empty map).
  useEffect(() => {
    if (!auth.isAuthed || !hydrated) return;
    if (skipNextDiffRef.current) {
      skipNextDiffRef.current = false;
      lastSyncedMapRef.current = buildValueMap(progress);
      return;
    }
    const lastMap = lastSyncedMapRef.current;
    if (lastMap.size === 0) {
      // First tick this session · establish the baseline, send nothing.
      lastSyncedMapRef.current = buildValueMap(progress);
      return;
    }

    const nextMap = new Map<string, string>();
    for (const { key, branch, id } of collectKeys(progress)) {
      const value = serialize(readNode(progress, branch, id));
      nextMap.set(key, value);
      if (lastMap.get(key) !== value) {
        // Real value change (or a newly-appeared key) → schedule. Pass a getter
        // that re-reads the LIVE value at send time (P2 · retry never ships a
        // stale snapshot). `branch`/`id` are per-iteration `const` bindings.
        progressSync.schedule(branch, () => readNode(progressRef.current, branch, id), id);
      }
    }
    lastSyncedMapRef.current = nextMap;
  }, [progress, auth.isAuthed, hydrated]);

  return {
    conflictKind,
    dismissConflict: () => setConflictKind(null),
    retryConflict: () => {
      setConflictKind(null);
      void progressSync.retryAll();
    },
    hydrated,
  };
}
